import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  ANOMALY_FINDING_SORTS,
  type AnomalyObservationDto,
  type AnomalyOverviewResponse,
} from '@oip/contracts';
import { DataGrid, type Column } from '../../components/common/DataGrid';
import { DetailCard } from '../../components/common/DetailCard';
import { cardIcon } from '../../components/common/cardIcons';
import { FilterDrawer } from '../../components/common/FilterDrawer';
import { SearchBox } from '../../components/common/SearchBox';
import { useListState } from '../../hooks/useListState';
import { weekday } from '../../utils/format';
import { useAuth } from '../../auth/AuthProvider';
import { endpoints } from '../../api/endpoints';
import { useTerm } from '../../units/useTerm';

/**
 * The findings, as a list somebody can actually work with.
 *
 * ⚠️ **A hand-rolled table, and it should not have been.** It was a fixed 50
 * rows with no paging, no sort, no search and no way to narrow by measurement —
 * on the one surface whose whole job is letting a reader find the row they care
 * about. `DataGrid` already carries column selection, density, CSV export,
 * sticky headers and the pager, and every other list in this product uses it;
 * a second table meant a second set of behaviours to keep in step.
 *
 * ⚠️ **Its own request, not a slice of the overview.** The cards above are
 * expensive — an outcome mix over the window, the six gates, the earliest
 * observed day — and paging or re-sorting would recompute all of it and make
 * them flicker for no reader's benefit.
 */
interface FindingsGridProps {
  /** The overview, only for the counts the list cannot know. */
  overview: AnomalyOverviewResponse | undefined;
  /** The window the cards are describing, so the two cannot disagree. */
  window: { days: number } | { from: string; to: string };
}

interface FindingFilters {
  search?: string;
  family?: string;
  includeUnreadable?: 'true';
}

/**
 * The sort the server will actually accept.
 *
 * ⚠️ **A persisted preference the server can no longer honour must not wedge
 * the page for ever.** `useListState` remembers the sort in localStorage, so a
 * word that was once sent — or one left behind by a renamed column — comes back
 * on every load and earns a 400 with no way for a reader to clear it: the list
 * is empty, so there is no header to click. Falling back to the default is the
 * same rule the dashboard applies to a stale branch id, which is validated
 * against the current list on every render rather than trusted.
 */
const SORTS: readonly string[] = ANOMALY_FINDING_SORTS;
function usableSort(sort: string | undefined): string {
  return sort && SORTS.includes(sort) ? sort : 'detectionScore';
}

export function FindingsGrid({ overview, window: win }: FindingsGridProps): JSX.Element {
  // The reader's own word for a plate or a licence. See `useTerm`.
  const { t, T } = useTerm();
  const { tenantId } = useAuth();
  const [drawer, setDrawer] = useState(false);

  const ls = useListState<FindingFilters>(
    { sort: 'detectionScore', direction: 'desc', filters: {} },
    { persistKey: 'anomaly-findings' },
  );
  const [draft, setDraft] = useState<FindingFilters>(ls.filters);

  const query = { ...win, ...ls.query, sort: usableSort(ls.sort) };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['fleet-anomaly-findings', tenantId, query],
    queryFn: () => endpoints.fleetAnomalyFindings(query),
    enabled: Boolean(tenantId),
  });

  /*
   * ⚠️ Only the families PRESENT in the window are offered, from the server.
   * A filter listing every declared family would offer choices that can only
   * ever return nothing — the rule the vehicle picker already follows by
   * listing what reported rather than what exists.
   */
  const families = data?.families ?? [];
  /*
   * ⚠️ Counted over the PAGE, which is what the sentence beside it claims — a
   * figure over the whole window would need the server to count it, and a page
   * figure presented as a window figure is the silent-truncation fault one
   * paragraph along.
   */
  const staleWeek = (data?.items ?? []).filter((i) => i.judgedUnderPreviousWeek).length;

  const columns: Column<AnomalyObservationDto>[] = useMemo(
    () => [
      {
        /*
         * ⚠️ **The key is `vehicleName`, because the key IS what the sort sends.**
         * `DataGrid.onSortChange` hands the COLUMN KEY straight to the query, so
         * a column keyed `vehicle` asked the server to sort by a word
         * `ANOMALY_FINDING_SORTS` has never heard of — 400 *"Request validation
         * failed"*, on the one control a reader presses first. Every sortable
         * key here is a member of that list and a case asserts it.
         */
        key: 'vehicleName',
        header: 'Vehicle',
        /*
         * ⚠️ NOT sortable, and that is a refusal rather than an omission. A
         * name is not a column on the observation table, so the server can
         * only order by a uuid; sorting the fetched page instead draws an
         * arrow claiming the whole list is ordered while page two starts the
         * alphabet again. The SEARCH is how a reader reaches one vehicle, and
         * unlike a page-local sort it covers every page.
         */
        disableHide: true,
        minWidth: 130,
        exportValue: (r) => r.vehicleName,
        render: (r) =>
          /*
           * ⚠️ A real ANCHOR, and NO link for a vehicle that has moved branches:
           * `/vehicles/:id` is tenant-scoped, so it would 404 "Vehicle not
           * found", which reads as DELETED rather than as somewhere else. The
           * day rides the link so the finding opens on the day it happened.
           */
          r.vehicleKnown ? (
            <Link
              component={RouterLink}
              to={`/vehicles/${r.vehicleId}?day=${r.day}`}
              underline="hover"
            >
              {r.vehicleName}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {r.vehicleName}
            </Typography>
          ),
      },
      {
        /*
         * ⚠️ Its OWN column now, not a caption under the name. A plate is what
         * is painted on the vehicle and written on the job sheet — as a column
         * it sorts, exports and can be hidden by a reader who works by name.
         */
        key: 'registration',
        header: T('registration'),
        width: 130,
        exportValue: (r) => r.vehicleRegistration ?? '',
        render: (r) => (
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {r.vehicleRegistration ?? '—'}
          </Typography>
        ),
      },
      {
        key: 'day',
        header: 'Day',
        sortable: true,
        width: 140,
        /*
         * ⚠️ The CSV keeps the ISO day ALONE — it is what the API accepts, what
         * the vehicle link carries and what a spreadsheet can sort. The weekday
         * is a reading aid, and a cell holding both would stop being a date.
         */
        exportValue: (r) => r.day,
        render: (r) => (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {r.day}
            </Typography>
            {/*
              ⚠️ The weekday, because whether an unusual day was a WORKING day is
              most of what decides whether it needs chasing — a quiet Saturday is
              a fleet at rest and a quiet Tuesday is a question. Named and NOT
              judged: which days a fleet works is tenant configuration, so
              marking a weekend here would be a second copy of that calendar and
              wrong for everyone who runs one.
            */}
            <Typography variant="caption" color="text.secondary" display="block">
              {weekday(r.day)}
            </Typography>
            {/*
              ⚠️ **Which population the day was compared against**, without which
              a per-vehicle working week is invisible magic: a reader cannot tell
              why a weekend lorry's Saturday is normal, nor connect a change in
              findings to somebody having edited a vehicle. Read as words —
              `NON_WORKING` is the engine's vocabulary, not a reader's.
            */}
            <Typography variant="caption" color="text.secondary" display="block">
              {r.context === 'WORKING'
                ? 'vs working days'
                : r.context === 'NON_WORKING'
                  ? 'vs non-working days'
                  : `vs ${r.context.replace(/_/g, ' ').toLowerCase()}`}
            </Typography>
            {/*
              ⚠️ **Without this the row CONTRADICTS the vehicle's own settings.**
              A calendar is not part of an observation's identity, so a re-run
              keeps the verdict reached at the time — correct as provenance, and
              it means a reader can see "Sunday · vs non-working days" on a
              vehicle whose Sunday is now a working day. Worse: somebody who
              corrected that vehicle's week precisely to fix bad findings watches
              them persist, with nothing saying a re-run will not help.

              ⚠️ It explains rather than warns. The finding is not WRONG — it is
              what the platform concluded under the week in force then — so
              flagging it as a fault would misdescribe a correct record.
            */}
            {/*
              ⚠️ ITALIC, because it annotates the line above it rather than
              adding a fourth fact to the cell. The day, the weekday and the
              comparison are three things about the row; this is a caveat on the
              third, and rendering it in the same weight made a four-line cell
              beside two-line neighbours — which reads as density rather than as
              structure.
            */}
            {r.judgedUnderPreviousWeek && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontStyle: 'italic' }}
              >
                under a previous working week
              </Typography>
            )}
          </>
        ),
      },
      {
        key: 'label',
        header: 'What was measured',
        /* ⚠️ Not sortable either — the label comes from the feature catalogue,
           not from a column. Narrow by Measurement in Filters instead, which
           the server applies to the whole window. */
        minWidth: 160,
        exportValue: (r) => r.label,
        render: (r) => (
          <>
            <Typography variant="body2">{r.label}</Typography>
            {r.family && (
              <Typography variant="caption" color="text.secondary" display="block">
                {r.family.replace(/_/g, ' ').toLowerCase()}
              </Typography>
            )}
          </>
        ),
      },
      {
        key: 'detectionScore',
        header: 'How unusual',
        sortable: true,
        align: 'right',
        width: 130,
        /*
         * ⚠️ **The SAME percentage the screen shows, not the raw 0–1 score.**
         * The cell reads 43% and the export read 0.427 — one quantity, two
         * surfaces, a factor of a hundred apart, under a header ("How unusual")
         * that names no unit. Reported from the downloaded file. `43%` is also
         * what every spreadsheet parses natively as a percentage, so the column
         * still sorts and charts; a bare `43` would need the unit in the header
         * and the header is shared with the screen, where the cell already
         * carries the sign.
         *
         * ⚠️ NULL stays EMPTY, never 0 or "0%". A blank cell is a spreadsheet's
         * "no value"; zero means "exactly typical", which is the opposite of
         * "could not say".
         */
        exportValue: (r) =>
          r.detectionScore === null ? '' : `${Math.round(r.detectionScore * 100)}%`,
        render: (r) =>
          /*
           * ⚠️ NULL is "could not say", never 0. Zero means "exactly typical" —
           * the opposite — and rendering them alike reports a refusal as a
           * perfect result.
           */
          r.detectionScore === null ? (
            <Typography variant="caption" color="text.secondary">
              not compared
            </Typography>
          ) : (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(r.detectionScore * 100)}%
            </span>
          ),
      },
      {
        key: 'reason',
        header: 'Why',
        minWidth: 320,
        exportValue: (r) => r.reason,
        render: (r) => r.reason,
      },
    ],
    // `T` is stable per locale (see `useTerm`), so the columns rebuild when the reader's
    // word for a plate changes and at no other time.
    [T],
  );

  const activeChips = Object.entries(ls.filters).filter(([k, v]) => v && k !== 'search');

  return (
    <DetailCard title="Findings" icon={cardIcon('risk')}>
      <DataGrid<AnomalyObservationDto>
        columns={columns}
        rows={data?.items ?? []}
        /*
         * ⚠️ The id is the FINDING, not the vehicle: one vehicle can have
         * several on one day, and keying on the vehicle would collapse them.
         */
        getRowId={(r) => `${r.vehicleId}-${r.featureId}-${r.day}`}
        loading={isLoading}
        error={error}
        onRetry={refetch}
        sort={usableSort(ls.sort)}
        direction={ls.direction}
        onSortChange={ls.onSortChange}
        page={ls.page}
        pageSize={ls.pageSize}
        totalItems={data?.totalItems ?? 0}
        totalPages={data?.totalPages ?? 1}
        onPage={ls.setPage}
        onPageSize={ls.setPageSize}
        /*
         * ⚠️ **`persistKey`, never a controlled view read straight out of
         * localStorage.** `useGridView` RECONCILES a stored view against the
         * current columns — dropping keys that have gone and appending ones
         * that are new — and holding the view myself skipped that entirely. So
         * renaming the Vehicle column's key to `vehicleName` left every reader
         * who had already loaded this page with a stored order naming
         * `vehicle`, and the column simply **did not render**: no error, no
         * empty cell, a table with one fewer column than it has. Reported from
         * the built page.
         */
        /*
         * ⚠️ **`-v2` because a column CHANGED IDENTITY, not because one was
         * added.** `reconcileView` handles an added column correctly — it
         * appends the new key and keeps the reader's own order — but a RENAME
         * looks identical to it: the old key is dropped and the new one lands
         * at the END, so Vehicle came back after Why. The stored order was
         * describing a different table. Bump the suffix only for that, and
         * never for a column merely added, or every reader loses an ordering
         * they chose on purpose.
         */
        persistKey="anomaly-findings-v2"
        exportFilename="anomaly-findings"
        label="Anomaly findings"
        emptyMessage="Nothing was judged unusual here. That is a measurement, not an absence — the counts above say how much was looked at."
        toolbar={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <SearchBox
              value={ls.filters.search ?? ''}
              onChange={(v) => ls.setFilter({ search: v || undefined })}
              placeholder={`Vehicle or ${t('registration')}…`}
            />
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              variant="outlined"
              onClick={() => {
                setDraft(ls.filters);
                setDrawer(true);
              }}
            >
              Filters{activeChips.length ? ` · ${activeChips.length}` : ''}
            </Button>
          </Stack>
        }
      />

      {/*
        ⚠️ A count the LIST cannot know, kept beside it. These rows sit on
        measurements no shipped family reads, so they could never become an
        incident — listing them by default would pad the page with items nobody
        can act on, and dropping them silently would hide that the catalogue has
        moved ahead of its families. The filter below is the way in.
      */}
      {/*
        ⚠️ Counted once BESIDE the grid rather than repeated per row, and it says
        the thing a per-row note cannot: that these will not change on a re-run.
        A reader who has just corrected a vehicle's week is otherwise left
        waiting for tomorrow's pass to fix them.
      */}
      {staleWeek > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {staleWeek} of these {staleWeek === 1 ? 'was' : 'were'} judged under a working week
          that has since changed. They are what the detector concluded at the time and are kept
          as a record — re-running will not re-judge them, and days from here on use the new
          week.
        </Typography>
      )}
      {data && data.notInAFamily > 0 && !ls.filters.includeUnreadable && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {data.notInAFamily} more {data.notInAFamily === 1 ? 'is' : 'are'} on measurements no
          family reads yet, so they could never become an incident. Not shown — use Filters to
          include them.
        </Typography>
      )}
      {overview && overview.evaluated === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The detector has not judged anything in this window.
        </Typography>
      )}

      <FilterDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        onApply={() => ls.setFilters(draft)}
        onClear={() => {
          setDraft({});
          ls.clearFilters();
        }}
      >
        <TextField
          select
          label="Measurement"
          size="small"
          fullWidth
          value={draft.family ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, family: e.target.value || undefined }))}
        >
          <MenuItem value="">Any</MenuItem>
          {families.map((f) => (
            <MenuItem key={f} value={f}>
              {f.replace(/_/g, ' ').toLowerCase()}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Measurements no family reads"
          size="small"
          fullWidth
          value={draft.includeUnreadable ?? ''}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              includeUnreadable: e.target.value === 'true' ? 'true' : undefined,
            }))
          }
          /*
           * ⚠️ `displayEmpty` and a shrunk label, because the EMPTY value is a
           * meaningful state — "hidden" is the default and MUI renders nothing
           * for an empty value unless told otherwise. The fault this product
           * has already recorded twice in one day.
           */
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Hidden (default)</MenuItem>
          <MenuItem value="true">Show them too</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary">
          These deviations are real, but no shipped family reads the measurement — so they could
          never become an incident even once publishing is switched on.
        </Typography>
      </FilterDrawer>
    </DetailCard>
  );
}
